import {
  Badge,
  Box,
  Checkbox,
  FormControl,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import PlusIcon from 'assets/add-white.svg';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import CustomSearch from 'components/shared/CustomSearch/CustomSearch';
import CustomTab from 'components/shared/CustomTab/CustomTab';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { IOSSwitch } from 'components/shared/IOSSwitch';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { TaskService } from 'service/task.service';
import zeplinColor from 'theme/Color';
import { LayoutType } from '../../TasksMain';
import { AddTaskForm } from './components/AddTaskForm';
import { TaskTypeSelectionModal } from './components/TaskTypeSelectionModal';
import styles from './TasksFilters.module.css';

const MenuProps = {
  PaperProps: {
    sx: {
      borderRadius: '14px',
    },
  },
};

const TIME_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'thisMonth', label: 'This Month' },
] as const;

const DEFAULT_PRIORITY_OPTIONS = ['Medium', 'Low', 'High'];

const EMPTY_FILTERS = {
  priority: [],
  time: [],
  client: [],
  recordTypes: [],
  assignedTo: [],
};

const COMMON_INPUT_STYLES = {
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
    color: zeplinColor.Neutral9,
    borderColor: zeplinColor.Neutral9,
  },
  padding: '0px 14px',
};

const COMMON_LABEL_STYLES = {
  fontSize: { xs: '14px', xl: '16px' },
  '&.Mui-focused': { color: zeplinColor.Neutral9 },
};

const COMMON_CHECKBOX_STYLES = {
  color: zeplinColor.BasicBlack,
  '&.Mui-checked': {
    color: zeplinColor.BasicBlack,
  },
};

export type TaskFilters = {
  priority: string[];
  time: string[];
  client: string[];
  recordTypes: string[];
  assignedTo?: string[];
  search?: string;
  needAttention?: boolean;
};

type SelectedItems = {
  priority: string[];
  time: string[];
  client: string[];
  recordTypes: string[];
  assignedTo: string[];
};

type TasksFiltersProps = {
  layout: LayoutType;
  onLayoutChange: (_: SyntheticEvent, newValue: LayoutType) => void;
  onTabChange?: (newValue: number) => void;
  tabValue?: number;
  onApplyFilters?: (filters: TaskFilters) => void;
  onRefresh?: () => void;
};

export const TasksFilters = ({
  layout,
  onLayoutChange,
  onTabChange,
  tabValue = 0,
  onApplyFilters,
  onRefresh,
}: TasksFiltersProps) => {
  const [value, setValue] = useState(tabValue);
  const [searchText, setSearchText] = useState<string>('');
  const [needsAttention, setNeedsAttention] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<SelectedItems>(EMPTY_FILTERS);
  const [clientList, setClientList] = useState<Array<{ id: string; name: string }>>([]);
  const [recordTypesOptions, setRecordTypesOptions] = useState<{ label: string; value: string }[]>(
    [],
  );
  const [priorityOptions, setPriorityOptions] = useState<string[]>(DEFAULT_PRIORITY_OPTIONS);
  const [assignedToOptions, setAssignedToOptions] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerState, setDrawerState] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState<string | null>(null);

  const tabs = useMemo(
    () => [
      {
        label: 'My Tasks',
        id: 1,
        isHidden: value !== 0,
        Component: <></>,
      },
      {
        label: 'All Tasks',
        id: 2,
        isHidden: value !== 1,
        Component: <></>,
      },
    ],
    [value],
  );

  const applyFilters = useCallback(
    (filters: Partial<TaskFilters>) => {
      if (onApplyFilters) {
        onApplyFilters({
          ...selectedItems,
          search: searchText,
          needAttention: needsAttention,
          ...filters,
        });
      }
    },
    [onApplyFilters, selectedItems, searchText, needsAttention],
  );

  const handleTabChange = useCallback(
    (_: SyntheticEvent, newValue: number) => {
      setValue(newValue);
      onTabChange?.(newValue);
    },
    [onTabChange],
  );

  useEffect(() => {
    setValue(tabValue);
  }, [tabValue]);

  const handleSearchTasks = useCallback(
    (text: string) => {
      setSearchText(text);
      applyFilters({ search: text });
    },
    [applyFilters],
  );

  const handleNeedsAttentionSwitch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setNeedsAttention(checked);
      applyFilters({ needAttention: checked });
    },
    [applyFilters],
  );

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleSelection = useCallback(
    (item: SelectChangeEvent<string | string[]>, type: keyof SelectedItems) => {
      const { value } = item.target;
      setSelectedItems((prev) => ({
        ...prev,
        [type]:
          typeof value === 'string' ? value.split(',') : Array.isArray(value) ? value : [value],
      }));
    },
    [],
  );

  const handleClearAll = useCallback(() => {
    setSelectedItems(EMPTY_FILTERS);
    setSearchText('');
    setNeedsAttention(false);
    setAnchorEl(null);
    if (onApplyFilters) {
      onApplyFilters({
        ...EMPTY_FILTERS,
        search: '',
        needAttention: false,
      });
    }
  }, [onApplyFilters]);

  const handleApplyFilters = useCallback(() => {
    applyFilters({});
    setAnchorEl(null);
  }, [applyFilters]);

  const getClient = useCallback(async () => {
    try {
      const res = await TaskService.getClientFilterValues();
      setClientList(res?.task_filter_values?.client || []);
      setRecordTypesOptions(res?.task_filter_values?.recordTypes || []);
      setAssignedToOptions(res?.task_filter_values?.assignedTo || []);
      if (res?.task_filter_values?.priority && res.task_filter_values.priority.length > 0) {
        setPriorityOptions(res.task_filter_values.priority);
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    getClient();
  }, [getClient]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleTaskCreateClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleTaskTypeSelect = useCallback((taskType: string) => {
    setSelectedTaskType(taskType);
    setModalOpen(false);
    setDrawerState(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerState(false);
    setSelectedTaskType(null);
  }, []);

  const handleTaskAdded = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    } else if (onApplyFilters) {
      onApplyFilters({
        ...selectedItems,
        search: searchText,
        needAttention: needsAttention,
      });
    }
  }, [onRefresh, onApplyFilters, selectedItems, searchText, needsAttention]);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const popoverId = useMemo(() => (open ? 'simple-popover' : undefined), [open]);
  const filterBadgeCount = useMemo(
    () => Object.values(selectedItems).reduce((prev, val) => (val?.length ? prev + 1 : prev), 0),
    [selectedItems],
  );
  const hasActiveFilters = useMemo(
    () => !Object.values(selectedItems).every((val) => val.length <= 0),
    [selectedItems],
  );

  const renderMultiSelectValue = useCallback(
    (selected: unknown, options: Array<{ id: string; name: string }>) => {
      const selectedIds = selected as string[];
      const selectedItems = options.filter((item) => selectedIds.includes(item.id));
      return selectedItems.map((item) => item.name).join(', ');
    },
    [],
  );

  const renderStringArrayValue = useCallback((selected: unknown) => {
    return (selected as string[]).join(', ');
  }, []);

  const renderTimeRangeValue = useCallback((selected: unknown) => {
    const selectedValue = (selected as string[])[0];
    const selectedOption = TIME_RANGE_OPTIONS.find((option) => option.value === selectedValue);
    return selectedOption ? selectedOption.label : '';
  }, []);

  const isChecked = useCallback((value: string, selectedArray: string[]) => {
    return selectedArray.indexOf(value) > -1;
  }, []);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Box>
        <CustomTab tabs={tabs} handleChange={handleTabChange} value={value} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <Box sx={{ width: '286px' }}>
          <CustomSearch getProjects={handleSearchTasks} />
        </Box>
        <Box>
          <Typography>Needs Attention</Typography>{' '}
          <IOSSwitch checked={needsAttention} onChange={handleNeedsAttentionSwitch} />
        </Box>

        <Badge
          badgeContent={filterBadgeCount}
          color='primary'
          sx={{ '.MuiBadge-badge': { top: '0.7rem', right: '0.2rem' } }}
        >
          <LightTooltip
            title={
              <Typography variant='body3' color={zeplinColor.Neutral1}>
                Filter
              </Typography>
            }
            placement='bottom-start'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
          >
            <IconButton
              sx={{
                width: { xs: '30px', xl: '50px' },
                height: { xs: '30px', xl: '50px' },
                border: `1px solid ${zeplinColor.Neutral6}`,
              }}
              onClick={handleClick}
            >
              <img src='/assets/filter.svg' alt='setting icon' />
            </IconButton>
          </LightTooltip>
        </Badge>

        <Box
          sx={{
            borderRadius: '50px',
            border: `1px solid ${zeplinColor.Neutral6}`,
            padding: '6px',
            width: { xs: '82px', xl: '92px' },
            height: { xs: '40px', xl: '48px' },
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            backgroundColor: zeplinColor.Neutral1,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: { xs: '35px', xl: '40px' },
              height: { xs: '28px', xl: '36px' },
              backgroundColor: zeplinColor.Brand1,
              opacity: 0.1,
              borderRadius: '40px',
              transition: 'transform 0.3s ease',
              transform:
                layout === LayoutType.KANBAN
                  ? 'translateX(0)'
                  : { xs: 'translateX(35px)', xl: 'translateX(40px)' },
              left: '6px',
            }}
          />

          <Box
            onClick={(e) => onLayoutChange(e, LayoutType.KANBAN)}
            sx={{
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '40px',
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <img
              src={
                layout === LayoutType.KANBAN
                  ? `/assets/selected-col-layout.svg`
                  : `/assets/column-layout-icon.svg`
              }
              className={layout === LayoutType.KANBAN ? styles['icons'] : styles['selected-icons']}
              alt='Column layout'
              style={{
                transition: 'all 0.2s ease',
                transform: layout === LayoutType.KANBAN ? 'scale(1.1)' : 'scale(1)',
              }}
            />
          </Box>

          <Box
            onClick={(e) => onLayoutChange(e, LayoutType.LIST)}
            sx={{
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '40px',
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <img
              src={
                layout === 'list'
                  ? `/assets/selected-row-layout.svg`
                  : `/assets/row-layout-icon.svg`
              }
              className={layout === 'list' ? styles['icons'] : styles['selected-icons']}
              alt='Row layout'
              style={{
                transition: 'all 0.2s ease',
                transform: layout === 'list' ? 'scale(1.1)' : 'scale(1)',
              }}
            />
          </Box>
        </Box>

        <CustomButton
          disabled={false}
          variant='contained'
          text='New Task'
          onClick={handleTaskCreateClick}
          icon={<img src={PlusIcon} alt='Add task' />}
          iconPosition='start'
          style={{
            borderRadius: '14px',
            height: '48px',
          }}
          buttonVariant='buttonMedium'
        />

        <CustomPopOver
          setAnchorEl={setAnchorEl}
          anchorEl={anchorEl}
          id={popoverId}
          sx={{
            borderRadius: '14px',
            boxShadow: 'rgba(0, 0, 0, 0.15)',
            marginTop: '8px',
            marginRight: '2rem',
            marginLeft: '-4rem',
          }}
        >
          <Box
            sx={{
              height: { xs: '22rem', xl: '27rem' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                padding: '16px',
                width: { xs: '250px', xl: '283px' },
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                flex: 1,
                overflow: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                  Filter
                </Typography>
                <Typography
                  variant='responsiveButtonSmall'
                  color={hasActiveFilters ? zeplinColor.Brand1 : zeplinColor.Neutral8}
                  onClick={hasActiveFilters ? handleClearAll : undefined}
                  sx={{
                    cursor: hasActiveFilters ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Clear All
                </Typography>
              </Box>

              <FormGroup
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                {value === 1 && (
                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel id='assignees-label' sx={COMMON_LABEL_STYLES}>
                      Assignees
                    </InputLabel>
                    <Select
                      labelId='assignees-label'
                      id='assignees-select'
                      multiple
                      value={selectedItems.assignedTo}
                      onChange={(e) => handleSelection(e, 'assignedTo')}
                      input={<OutlinedInput sx={COMMON_INPUT_STYLES} label='Assignees' />}
                      renderValue={(selected) =>
                        renderMultiSelectValue(selected, assignedToOptions)
                      }
                      MenuProps={MenuProps}
                      IconComponent={() => (
                        <img src='/assets/down-arrow.svg' className={styles['icon-24']} />
                      )}
                    >
                      {assignedToOptions.map((assignee) => (
                        <MenuItem
                          key={assignee.id}
                          value={assignee.id}
                          sx={{
                            fontSize: { xs: '14px', xl: '16px' },
                            padding: { xs: '6px 16px', xl: '8px 16px' },
                          }}
                        >
                          <Checkbox
                            checked={isChecked(assignee.id, selectedItems.assignedTo)}
                            sx={COMMON_CHECKBOX_STYLES}
                          />
                          {assignee.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <FormControl sx={{ width: '100%' }}>
                  <InputLabel id='record-types-label' sx={COMMON_LABEL_STYLES}>
                    Record Types
                  </InputLabel>
                  <Select
                    labelId='record-types-label'
                    id='record-types-select'
                    multiple
                    value={selectedItems.recordTypes}
                    onChange={(e) => handleSelection(e, 'recordTypes')}
                    input={<OutlinedInput sx={COMMON_INPUT_STYLES} label='Record Types' />}
                    renderValue={renderStringArrayValue}
                    MenuProps={MenuProps}
                    IconComponent={() => (
                      <img src='/assets/down-arrow.svg' className={styles['icon-24']} />
                    )}
                  >
                    {recordTypesOptions.map(({ label, value }) => (
                      <MenuItem
                        key={label}
                        value={value}
                        sx={{
                          fontSize: { xs: '12px', xl: '16px' },
                          padding: { xs: '6px 16px', xl: '8px 16px' },
                        }}
                      >
                        <Checkbox
                          checked={isChecked(value, selectedItems.recordTypes)}
                          sx={COMMON_CHECKBOX_STYLES}
                        />
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ width: '100%' }}>
                  <InputLabel id='priority-label' sx={COMMON_LABEL_STYLES}>
                    Priority
                  </InputLabel>
                  <Select
                    labelId='priority-label'
                    id='priority-select'
                    multiple
                    value={selectedItems.priority}
                    onChange={(e) => handleSelection(e, 'priority')}
                    input={<OutlinedInput sx={COMMON_INPUT_STYLES} label='Priority' />}
                    renderValue={renderStringArrayValue}
                    MenuProps={MenuProps}
                    IconComponent={() => (
                      <img src='/assets/down-arrow.svg' className={styles['icon-24']} />
                    )}
                  >
                    {priorityOptions.map((name) => (
                      <MenuItem
                        key={name}
                        value={name}
                        sx={{
                          fontSize: { xs: '14px', xl: '16px' },
                          padding: { xs: '6px 16px', xl: '8px 16px' },
                        }}
                      >
                        <Checkbox
                          checked={isChecked(name, selectedItems.priority)}
                          sx={COMMON_CHECKBOX_STYLES}
                        />
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ width: '100%' }}>
                  <InputLabel id='client-label' sx={COMMON_LABEL_STYLES}>
                    Client
                  </InputLabel>
                  <Select
                    labelId='client-label'
                    id='client-select'
                    multiple
                    value={selectedItems.client}
                    onChange={(e) => handleSelection(e, 'client')}
                    input={<OutlinedInput sx={COMMON_INPUT_STYLES} label='Client' />}
                    IconComponent={() => (
                      <img src='/assets/down-arrow.svg' className={styles['icon-24']} />
                    )}
                    renderValue={(selected) => renderMultiSelectValue(selected, clientList)}
                    MenuProps={MenuProps}
                  >
                    {clientList?.map((client) => (
                      <MenuItem
                        key={client.id}
                        value={client.name}
                        sx={{
                          fontSize: { xs: '14px', xl: '16px' },
                          padding: { xs: '6px 16px', xl: '8px 16px' },
                        }}
                      >
                        <Checkbox
                          checked={isChecked(client.id, selectedItems.client)}
                          sx={COMMON_CHECKBOX_STYLES}
                        />
                        {client.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ width: '100%' }}>
                  <InputLabel id='time-range-label' sx={COMMON_LABEL_STYLES}>
                    Time range
                  </InputLabel>
                  <Select
                    labelId='time-range-label'
                    id='time-range-select'
                    value={selectedItems.time}
                    onChange={(e) => handleSelection(e, 'time')}
                    input={<OutlinedInput sx={COMMON_INPUT_STYLES} label='Time range' />}
                    IconComponent={() => (
                      <img src='/assets/down-arrow.svg' className={styles['icon-24']} />
                    )}
                    renderValue={renderTimeRangeValue}
                    MenuProps={MenuProps}
                  >
                    {TIME_RANGE_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{ fontSize: { xs: '14px', xl: '16px' } }}
                      >
                        <Radio
                          checked={selectedItems.time[0] === option.value}
                          sx={{
                            color: zeplinColor.BasicBlack,
                            '&.Mui-checked': {
                              color: zeplinColor.BasicBlack,
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: { xs: '18px', xl: '20px' },
                            },
                          }}
                        />
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FormGroup>
            </Box>

            <Box
              sx={{
                padding: '0 16px 16px 16px',
                width: { xs: '250px', xl: '283px' },
              }}
            >
              <CustomButton
                disabled={false}
                variant='contained'
                text='Apply'
                onClick={handleApplyFilters}
                style={{
                  borderRadius: '14px',
                  height: '48px',
                  width: '100%',
                }}
                buttonVariant='buttonMedium'
              />
            </Box>
          </Box>
        </CustomPopOver>

        <TaskTypeSelectionModal
          isModalOpen={modalOpen}
          onClose={handleModalClose}
          onSelect={handleTaskTypeSelect}
        />

        {drawerState && selectedTaskType && (
          <AddTaskForm
            drawerState={drawerState}
            onClose={handleDrawerClose}
            taskType={selectedTaskType}
            onTaskAdded={handleTaskAdded}
          />
        )}
      </Box>
    </Box>
  );
};
